<template>
  <Dialog v-model="currentVisible" :title="$t('Multi-factor authentication')" width="650px">
    <Row>
      <Col md="7">
        <p class="q-mt-md">
          {{
            $t(
              'Use a multi-factor authentication application on your phone to scan the QR code. Enter the multi-factor authentication codes in the fields at the bottom.'
            )
          }}
        </p>
        <p class="text-weight-bold">
          {{ $t('If you can’t use a QR code, enter this secret instead.') }}
        </p>
        <p>
          {{ secret }}
        </p>
      </Col>
      <Col md="5">
        <img :src="qrCode" />
      </Col>
    </Row>
    <div class="text text-weight-bold">
      {{
        $t(
          'Recovery codes are used to access your account in the event you cannot receive multi-factor authentication codes.'
        )
      }}
    </div>
    <div class="q-mb-md q-pt-sm">
      <div v-for="code in recoveryCodes" :key="code">{{ code }}</div>
    </div>

    <form @submit.prevent="onSubmit">
      <Input
        name="passcode"
        :label="$t('Passcode')"
        :max-length="20"
        v-model="formData.passcode"
        required
      />

      <Banner type="danger" icon v-if="errorMsg">{{ errorMsg }}</Banner>

      <div class="q-mt-sm text-right q-gutter-sm">
        <Button :label="$t('Cancel')" flat @click="currentVisible = false" />
        <Button :label="$t('Verify')" type="submit" :disabled="loading" :loading="loading" />
      </div>
    </form>
  </Dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Button from '@design-system/components/Button.vue';
import Dialog from '@design-system/components/Dialog.vue';
import Row from '@design-system/components/Row.vue';
import Col from '@design-system/components/Col.vue';
import Form from '@design-system/components/Form.vue';
import Input from '@design-system/components/Input.vue';
import Banner from '@design-system/components/Banner.vue';

const messages = {
  en: {
    'Multi-factor authentication': 'Multi-factor authentication',
    'Use a multi-factor authentication application on your phone to scan the QR code. Enter the multi-factor authentication codes in the fields at the bottom.':
      'Use a multi-factor authentication application on your phone to scan the QR code. Enter the multi-factor authentication codes in the fields at the bottom.',
    'If you can’t use a QR code, enter this secret instead.':
      'If you can’t use a QR code, enter this secret instead.',
    'Recovery codes are used to access your account in the event you cannot receive multi-factor authentication codes.':
      'Recovery codes are used to access your account in the event you cannot receive multi-factor authentication codes.',
    Passcode: 'Passcode',
    'Please enter the passcode from your device.': 'Please enter the passcode from your device.',
    'Second passcode': 'Second passcode',
    'Please enter the second passcode from your device.':
      'Please enter the second passcode from your device.',
    Cancel: 'Cancel',
    Verify: 'Verify',
    'Error verifying MFA': 'Error verifying MFA',
    'Activation successful.': 'Activation successful.',
  },
  ja: {
    'Multi-factor authentication': '2段階認証',
    'Use a multi-factor authentication application on your phone to scan the QR code. Enter the multi-factor authentication codes in the fields at the bottom.':
      'お持ちの2段階認証アプリを使いQRコードをスキャンしてください。 コードが生成されますので、下の欄に入力。',
    'If you can’t use a QR code, enter this secret instead.':
      'QRコードがご使用になれない場合は、下記のシークレットコードを入力してください。',
    'Recovery codes are used to access your account in the event you cannot receive multi-factor authentication codes.':
      '復元コードは2段階認証で確認コードが取得できないときに使用します。',
    Passcode: '認証コード',
    'Please enter the passcode from your device.': '認証コードを入力してください。',
    Cancel: 'キャンセル',
    Verify: '確認',
    'Error verifying MFA': '認証コードの確認が取れませんでした。',
    'Activation successful.': '2段階認証が有効になりました。',
  },
};

export default {
  name: 'MyAccountMfaDialogActive',
  components: { Button, Dialog, Row, Col, Form, Input, Banner },
  props: { value: Boolean },
  i18n: { messages },
  data() {
    return {
      loading: false,
      errorMsg: null,
      formData: {
        passcode: '',
      },
    };
  },
  watch: {
    formData: {
      handler: function() {
        this.errorMsg = null;
      },
      deep: true,
    },

    'formData.passcode': function(value) {
      this.formData.passcode = value.replace(/\s+/g, '');
    },
  },
  computed: {
    ...mapState('mfa', {
      qrCode: state => `data:image/png;base64, ${state.data.qr_code}`,
      secret: state => state.data.secret,
      recoveryCodes: state => state.data.recovery_codes,
    }),

    currentVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  methods: {
    ...mapActions('mfa', {
      postVerify: 'postVerify',
    }),

    async onSubmit() {
      try {
        // Log
        this.$alpha.logger.addBreadcrumb('Click Activation button.');

        // Start Request
        await this.postVerify({
          passcode1: this.formData.passcode,
        });

        // After Request
        this.currentVisible = false;
        await this.$alpha.notify.custom({
          message: this.$t('Activation successful.'),
          color: 'success',
        });
      } catch (error) {
        this.errorMsg = this.$t('Error verifying MFA');
      }
    },
  },
};
</script>
